import cn from 'classnames'
import type {TrackingProps} from 'src/common/helpers'
import {EVENTS, track} from 'src/common/helpers'
import {useTranslate} from 'src/common/i18n/useTranslate'
import {Pill} from './Pill'

export type SelectorCardProps = {
  name: string
  title: string
  description: string
  isSelected: boolean
  isAvailable: boolean
  isRecommended: boolean
  onSelect: () => void
  trackingData: Omit<TrackingProps, 'event'>
}

export const SelectorCard = ({
  name,
  title,
  description,
  isSelected,
  isAvailable,
  isRecommended,
  onSelect,
  trackingData,
}: SelectorCardProps) => {
  const translate = useTranslate()

  const handleSelect = () => {
    track({event: EVENTS.CLICK, ...trackingData})
    onSelect()
  }

  return (
    <label
      className={cn(
        'flex min-w-[140px] flex-1 basis-[140px] cursor-pointer flex-col gap-2 rounded-card border p-6 transition-colors sm:min-w-[200px] sm:basis-[200px]',
        'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-green-neon',
        !isAvailable && 'cursor-not-allowed opacity-50',
        isAvailable && !isSelected && 'border-moss-white/[12%] hover:border-green-neon/35 hover:bg-green-neon/[4%]',
        isSelected && 'border-green-neon/[55%] shadow-[0_0_0_1px_rgba(185,255,63,0.55)]',
      )}
    >
      <input
        type="radio"
        name={name}
        checked={isSelected}
        disabled={!isAvailable}
        onChange={handleSelect}
        className="sr-only"
      />
      <div className="flex items-center gap-2">
        {!isAvailable && <Pill muted>{translate('discoveryBlock.comingSoon')}</Pill>}
        {isAvailable && isRecommended && <Pill>{translate('discoveryBlock.recommended')}</Pill>}
        <span
          className={cn(
            'ml-auto size-4 shrink-0 rounded-full border',
            isSelected
              ? 'border-green-neon bg-[radial-gradient(circle,_#B9FF3F_40%,_transparent_42%)]'
              : 'border-moss-white/30',
          )}
        />
      </div>
      <div className="flex min-w-0 flex-col">
        <span className="break-words text-14 font-bold leading-tight">{title}</span>
        <span className="mt-1 text-12 text-green-mist-dim">{description}</span>
      </div>
    </label>
  )
}
